/**
 * The authenticated area: boot gate (§12.1) + the single SSE subscription (§5.5) + the slot the
 * feature screens render into.
 *
 * Boot sequence: call `GET /api/auth/me`; `401` redirects to /login remembering where the user
 * was headed; any other failure keeps the shell up (the banner in `RootLayout` explains it) and
 * offers an inline retry.
 */

import { Navigate, Outlet, useLocation } from 'react-router';
import { ErrorState, Spinner } from '../components';
import { CommentChatProvider } from '../features/ai/CommentChatProvider';
import { SaveDialogProvider } from '../features/saved/SaveDialog';
import { useServerEvents } from '../sse';
import { APP_NAME } from './appTitle';
import { LOGIN_PATH } from './routes';
import { useAuth } from './useAuth';

/**
 * Rendered only once authenticated, so the EventSource never opens on a dead session.
 *
 * The save popup is hosted here rather than per screen (SPEC `saved-posts`): every card surface can
 * open it — the feed, a view's Feed tab, the notification list, the homescreen strip, the Posts
 * table — and one host above all of them means one `openSave(postId)` and one dialog, stackable
 * over the post preview instead of trapped inside whichever card raised it. The comment chat
 * (SPEC `Comment Generation`) is hosted for exactly the same reason, from the same set of surfaces.
 */
function AuthedShell() {
  useServerEvents();
  return (
    <SaveDialogProvider>
      <CommentChatProvider>
        <main className="app-main">
          <Outlet />
        </main>
      </CommentChatProvider>
    </SaveDialogProvider>
  );
}

export function AuthedLayout() {
  const location = useLocation();
  const { isBooting, authenticated, isUnauthenticated, query } = useAuth();

  if (isBooting) {
    return (
      <div className="center-fill">
        <Spinner size="lg" label={`Starting ${APP_NAME}…`} />
      </div>
    );
  }

  if (isUnauthenticated) {
    return <Navigate to={LOGIN_PATH} replace state={{ from: location.pathname + location.search }} />;
  }

  if (!authenticated) {
    return (
      <div className="center-fill">
        <ErrorState
          title="Can't reach the server"
          error={query.error}
          onRetry={() => void query.refetch()}
          retrying={query.isFetching}
        />
      </div>
    );
  }

  return <AuthedShell />;
}

export default AuthedLayout;
